import { clearIndexedDB, saveToIndexedDB } from './IndexedDBUtils';
import { normalizeVerbatimLink } from './VerbatimUtils';

/**
 * Exports the current project configuration to a JSON file.
 * @param {Object} projectData - The project data to export.
 * @param {Function} promptUser - Function to prompt the user for a filename.
 */
export const handleExportConfiguration = (projectData, promptUser = window.prompt) => {
  
  const { verbatims, notes, slides, labels, labelTypes } = projectData;

  const filename = promptUser("Please enter a name for the export file:", "dynvibe-project");

  if (filename) {
    let finalFilename = filename.toLowerCase().endsWith('.json') ? filename : `${filename}.json`;
    const data = { verbatims, notes, slides, labels, labelTypes };
    const json = JSON.stringify(data);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalFilename;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    alert("Export canceled. No filename provided.");
  }
};

/**
 * Exports the verbatims to a tx file.
 * @param {Object} projectData - The project data to export.
 * @param {Function} promptUser - Function to prompt the user for a filename.
 */
export const handleExportVerbatimConfiguration = (projectData, promptUser = window.prompt) => {
  const { verbatims, selectedLabelTypes = [] } = projectData;
  const filename = promptUser("Please enter a name for the export file:", "dynvibe-project");
  
  if (filename) {
    let finalFilename = filename.toLowerCase().endsWith('.txt') ? filename : `${filename}.txt`;
    const content = Object.keys(verbatims)
      .filter((key) => typeof verbatims[key].verbatim === 'string' || (Array.isArray(verbatims[key].verbatim) && verbatims[key].verbatim[0].content.trim() !== ''))
      .map(key => {
        const verbatimText =
          typeof verbatims[key].verbatim === 'string'
            ? verbatims[key].verbatim
            : verbatims[key].verbatim[0].content;

        let labelTypesText = '';

        if (selectedLabelTypes && selectedLabelTypes.length > 0) {
          labelTypesText = selectedLabelTypes
            .map((labelType) => {
              if (verbatims[key]?.labels?.[labelType]) {
                const labelsForType = verbatims[key].labels[labelType].map(
                  (label) => `${label.name} (${labelType})`
                );
                return labelsForType.join(', ');
              }
              return null;
            })
            .filter(Boolean)
            .join(', ');
        }

        return labelTypesText
          ? `${verbatimText}\n\nlabels: ${labelTypesText}`
          : verbatimText;
      })
      .join('\n########################\n');
      
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalFilename;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    alert("Export canceled. No filename provided.");
  }
};

/**
 * Resets the project to its default state.
 * @param {Object} handlers - State update handlers for the project.
 * @param {Function} confirmReset - Function to confirm the reset action.
 */
export const handleResetConfiguration = (handlers, confirmReset = window.confirm) => {
  if (confirmReset("Are you sure you want to reset the project? This action will delete existing labels, stored verbatims, and slides.")) {
    handlers.setVerbatims([]);
    handlers.setNotes([]);
    handlers.setSlides([]);
    handlers.setLabels({});
    handlers.setLabelTypes({});
    
    // Vider complètement IndexedDB comme demandé
    clearIndexedDB();
  }
};

/**
 * Loads a project configuration from a JSON file.
 * @param {File} file - The file object selected by the user.
 * @param {Object} handlers - State update handlers for the project.
 * @param {Function} onError - Callback to handle errors during file reading.
 */
export const handleLoadConfiguration = (
  dataOrFile,
  handlers,
  onError = (e) => alert(e.message),
  options = {}
) => {
  try {
    const processData = (data) => {
      const { action, selectedLabels, selectedLabelTypes, selectedSlides } = options;
      const normalizedVerbatims = (data.verbatims || []).map((verbatim) => ({
        ...verbatim,
        link: normalizeVerbatimLink(verbatim?.link),
      }));

      if (action === 'overwrite') {
        // Check if IDs are sequential starting from 1 (i.e., [1, 2, 3, ...])
        const isSequential = normalizedVerbatims
          .map(item => item.id)
          .sort((a, b) => a - b)
          .every((id, index) => id === index + 1);

        // If not sequential, reassign IDs starting from 1
        const verbatimsToStore = isSequential
          ? normalizedVerbatims
          : normalizedVerbatims.map((item, index) => ({ ...item, id: index + 1 }));

        // Overwrite entire project data
        handlers.setVerbatims(verbatimsToStore);
        handlers.setNotes(data.notes || []);
        handlers.setSlides(selectedSlides || []);
        handlers.setLabels(selectedLabels || {});
        
        handlers.setLabelTypes(selectedLabelTypes || {});
        
        const defaultFilters = {
          source: '',
          dynamicFilters: {}
        };
        saveToIndexedDB('appSettings', defaultFilters, 'filters');
      } else if (action === 'append') {
        // Append selected data to existing project data
        handlers.setVerbatims((prev) => { 
          const maxId = Math.max(...(prev || []).map(v => v.id), 0);
          const newVerbatims = normalizedVerbatims.map((verbatim, index) => ({
            ...verbatim,
            id: maxId + index + 1
          }));

          return [...prev, ...newVerbatims]
        });
        handlers.setNotes((prev) => [...prev, ...(data.notes || [])]);
        handlers.setSlides((prev) => {
          const maxId = Math.max(...(prev || []).map(v => v.id), 0);
          const newSelectedSlides = (selectedSlides || []).map((slide, index) => ({
            ...slide,
            id: maxId + index + 1
          }));
          
          return [...prev, ...newSelectedSlides];
        });
        handlers.setLabels((prev) => {
          const updatedLabels = { ...prev };

          Object.keys(selectedLabels || {}).forEach((key) => {
            updatedLabels[key] = [
              ...(updatedLabels[key] || []),
              ...(selectedLabels[key] || [])?.filter(
                (label) =>
                  !updatedLabels[key]?.some(
                    (existingLabel) => existingLabel.name === label.name
                  )
              )
            ];
          });

          return updatedLabels;
        });
        handlers.setLabelTypes((prev) => ({ ...prev, ...selectedLabelTypes }));
      }
      
      // S'assurer que les paramètres d'autosauvegarde sont correctement initialisés après chargement
      import('./IndexedDBUtils').then(({ initializeDefaultSettings }) => {
        initializeDefaultSettings();
      });
    };

    // Handle data based on its type
    if (dataOrFile instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          processData(data);
        } catch (error) {
          onError(error);
        }
      };
      reader.readAsText(dataOrFile);
    } else {
      processData(dataOrFile); // If already-parsed data is provided
    }
  } catch (error) {
    onError(error);
  }
};
